import { apiClient } from '@/lib/api-client';
import {
  completeGithubLink,
  connectGithub,
  connectGithubManual,
  disconnectGithub,
  getGithubImportedUsers,
  getGithubLinkStatus,
  getGithubStatus,
  getGithubSyncStatus,
  initiateGithubLink,
  syncGithub,
  unlinkGithubLink,
} from '@kirin/api-client/api/github';
import type {
  GithubConnectResult,
  GithubDisconnectResult,
  GithubImportedUsersResult,
  GithubLinkCallbackRequest,
  GithubLinkCallbackResult,
  GithubLinkInitiateResult,
  GithubLinkStatusResponse,
  GithubManualConnectResult,
  GithubStatusResponse,
  GithubSyncResult,
  GithubSyncStatusResponse,
  GithubUnlinkResult,
} from '@kirin/api-client/contracts/github';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

/**
 * Shared query key factories for GitHub integration queries.
 * Centralises key definitions so invalidation stays consistent.
 */
const githubQueryKeys = {
  status: (orgId: string): readonly string[] => ['github-status', orgId],
  syncStatus: (orgId: string): readonly string[] => [
    'github-sync-status',
    orgId,
  ],
  importedUsers: (orgId: string): readonly string[] => [
    'github-imported-users',
    orgId,
  ],
} as const;

/**
 * Invalidate all GitHub-related queries for an organisation.
 * Used after any mutation that changes connection state (connect, disconnect, sync).
 */
function invalidateAllGithubQueries(
  queryClient: QueryClient,
  organizationId: string
): void {
  queryClient.invalidateQueries({
    queryKey: githubQueryKeys.status(organizationId),
  });
  queryClient.invalidateQueries({
    queryKey: githubQueryKeys.syncStatus(organizationId),
  });
  queryClient.invalidateQueries({
    queryKey: githubQueryKeys.importedUsers(organizationId),
  });
}

export function useGithubStatus(organizationId: string) {
  return useQuery({
    queryKey: githubQueryKeys.status(organizationId),
    queryFn: async (): Promise<GithubStatusResponse> => {
      return getGithubStatus(apiClient, organizationId);
    },
    enabled: !!organizationId,
  });
}

export function useGithubConnect(organizationId: string) {
  return useMutation({
    mutationFn: async (): Promise<GithubConnectResult> => {
      return connectGithub(apiClient, organizationId);
    },
  });
}

export function useGithubConnectManual(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      installationId: string
    ): Promise<GithubManualConnectResult> => {
      return connectGithubManual(apiClient, organizationId, {
        installation_id: installationId,
      });
    },
    onSuccess: () => {
      invalidateAllGithubQueries(queryClient, organizationId);
    },
  });
}

export function useGithubDisconnect(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<GithubDisconnectResult> => {
      return disconnectGithub(apiClient, organizationId);
    },
    onSuccess: () => {
      invalidateAllGithubQueries(queryClient, organizationId);
      // Clear cached imported users immediately on disconnect.
      // Invalidation alone won't refetch while the query is disabled.
      queryClient.removeQueries({
        queryKey: githubQueryKeys.importedUsers(organizationId),
      });
    },
  });
}

export function useGithubSyncStatus(
  organizationId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: githubQueryKeys.syncStatus(organizationId),
    queryFn: async (): Promise<GithubSyncStatusResponse> => {
      return getGithubSyncStatus(apiClient, organizationId);
    },
    enabled: options?.enabled !== false && !!organizationId,
    // Refetch every 5 seconds when sync is running
    refetchInterval: query => {
      const data = query.state.data;
      return data?.isRunning ? 5000 : false;
    },
  });
}

export function useGithubSync(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<GithubSyncResult> => {
      return syncGithub(apiClient, organizationId);
    },
    onSuccess: () => {
      invalidateAllGithubQueries(queryClient, organizationId);
    },
  });
}

export function useGithubImportedUsers(
  organizationId: string,
  options?: { enabled?: boolean }
): UseQueryResult<GithubImportedUsersResult> {
  return useQuery({
    queryKey: githubQueryKeys.importedUsers(organizationId),
    queryFn: async (): Promise<GithubImportedUsersResult> => {
      return getGithubImportedUsers(apiClient, organizationId);
    },
    enabled: options?.enabled !== false && !!organizationId,
  });
}

// ============================================================================
// ACCOUNT LINKING
// ============================================================================

/**
 * Get the current user's GitHub link status
 */
export function useGithubLinkStatus(
  organizationId: string,
  options?: { enabled?: boolean }
): UseQueryResult<GithubLinkStatusResponse> {
  return useQuery({
    queryKey: ['github-link-status', organizationId],
    queryFn: async (): Promise<GithubLinkStatusResponse> => {
      return getGithubLinkStatus(apiClient, organizationId);
    },
    enabled: options?.enabled !== false && !!organizationId,
  });
}

/**
 * Initiate GitHub account linking
 * Returns the OAuth URL to redirect the user to
 */
export function useGithubLinkInitiate(
  organizationId: string
): UseMutationResult<GithubLinkInitiateResult, Error, void> {
  return useMutation({
    mutationFn: async (): Promise<GithubLinkInitiateResult> => {
      return initiateGithubLink(apiClient, organizationId);
    },
  });
}

/**
 * Complete GitHub account linking after OAuth callback
 */
export function useGithubLinkComplete(
  organizationId: string
): UseMutationResult<
  GithubLinkCallbackResult,
  Error,
  GithubLinkCallbackRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: GithubLinkCallbackRequest
    ): Promise<GithubLinkCallbackResult> => {
      return completeGithubLink(apiClient, organizationId, data);
    },
    onSuccess: () => {
      // Invalidate link status to reflect the new linked state
      queryClient.invalidateQueries({
        queryKey: ['github-link-status', organizationId],
      });
      // Also invalidate sync status as linked count may have changed
      queryClient.invalidateQueries({
        queryKey: ['github-sync-status', organizationId],
      });
      // And imported users since one is now linked
      queryClient.invalidateQueries({
        queryKey: ['github-imported-users', organizationId],
      });
      // Invalidate members list since it includes linkedGithub data
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Unlink the current user's GitHub account
 * Allows users to unlink their own account so they can link a different one
 */
export function useGithubLinkUnlink(
  organizationId: string
): UseMutationResult<GithubUnlinkResult, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<GithubUnlinkResult> => {
      return unlinkGithubLink(apiClient, organizationId);
    },
    onSuccess: () => {
      // Invalidate link status to reflect the unlinked state
      queryClient.invalidateQueries({
        queryKey: ['github-link-status', organizationId],
      });
      // Also invalidate sync status as linked count may have changed
      queryClient.invalidateQueries({
        queryKey: ['github-sync-status', organizationId],
      });
      // And imported users since one is now unlinked
      queryClient.invalidateQueries({
        queryKey: ['github-imported-users', organizationId],
      });
      // Invalidate members list since it includes linkedGithub data
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}
